import type { Language } from '@seek/melways-sites';

import {
  Actions as NextRoleActions,
  type NextRoleAction,
} from 'src/components/Profile/NextRole/reducer';
import { ADD, EDIT } from 'src/consts';
import type { Analytics } from 'src/middlewares/createAnalyticsMiddleware';
import { getDataFromCache } from 'src/modules/apollo/apolloCache';
import type { AppConfig } from 'src/modules/appConfig/types';
import type { Action, ApiClient } from 'src/types';
import * as Schema from 'src/types/graphql';

import { instrumentAnalytics } from '../analyticsMiddlewareUtils';

export const createNextRoleAnalyticsMiddleware = (
  analytics: Analytics,
  { apolloClient }: ApiClient,
  appConfig: AppConfig,
) =>
  instrumentAnalytics(analytics) &&
  (() => (next: (action: Action) => void) => (action: NextRoleAction) => {
    const { updateCard } = analytics;

    const getPreferredClassificationData = () =>
      getDataFromCache({
        cache: apolloClient.cache,
        query: Schema.GetPreferredClassificationDocument,
      });

    const getAvailabilityData = (languageCode: Language) =>
      getDataFromCache({
        cache: apolloClient.cache,
        query: Schema.GetAvailabilityDocument,
        variables: { languageCode },
      });

    switch (action.type) {
      case NextRoleActions.NEXT_ROLE_UPDATE_WORKTYPE_START: {
        const workTypesData = getDataFromCache({
          cache: apolloClient.cache,
          query: Schema.GetWorkTypesDocument,
        });
        const workTypes = workTypesData?.viewer?.workTypes || [];
        const updateAction = workTypes.length > 0 ? EDIT : ADD;

        updateCard({
          actionName: updateAction,
          currentPageSection: 'next role',
          customProps: {
            currentPageSubsection: 'work type',
          },
        });
        break;
      }

      case NextRoleActions.NEXT_ROLE_UPDATE_AVAILABILITY_START: {
        const { language } = appConfig;
        const availabilityData = getAvailabilityData(language);

        const currentAvailability =
          availabilityData?.viewer?.availability?.id || null;

        const updateAction = Boolean(currentAvailability) ? EDIT : ADD;

        updateCard({
          actionName: updateAction,
          currentPageSection: 'next role',
          customProps: {
            currentPageSubsection: 'availability',
          },
        });
        break;
      }

      case NextRoleActions.NEXT_ROLE_UPDATE_CLASSIFICATION_START: {
        const preferredClassificationData = getPreferredClassificationData();
        const currentPreferredClassification =
          preferredClassificationData?.viewer?.preferredClassification || null;
        const updateAction = Boolean(currentPreferredClassification)
          ? EDIT
          : ADD;

        updateCard({
          actionName: updateAction,
          currentPageSection: 'next role',
          customProps: {
            currentPageSubsection: 'classification',
          },
        });
        break;
      }

      case NextRoleActions.NEXT_ROLE_UPDATE_APPROACHABILITY_START: {
        const { approachable } = action.payload;

        const profileApproachabilityStatus = approachable ? 'on' : 'off';

        updateCard({
          actionName: EDIT,
          currentPageSection: 'next role',
          customProps: {
            currentPageSubsection: 'approachability',
            profileApproachabilityStatus,
          },
        });
        break;
      }

      default: {
        break;
      }
    }

    return next(action);
  });
